const React = require('react')
const { Route, IndexRoute, Redirect } = require('react-router')
const App = require('./app')
const redirect = require('lib/redirect')
const isUser = require('module/user/helpers/is_user')
const isLoggedIn = require('module/viewer/helpers/is_logged_in')
const isAdmin = require('module/viewer/helpers/is_admin')
const _ = require('lib/underscore')
const Query = require('lib/query')
const funnels = require('lib/funnels')
const log = require('log').create('routes') // eslint-disable-line

function getDefaultRoutes(options) {
    const viewer = options.viewer
    const logged_in = isLoggedIn(viewer)
    const is_production = options.globals.ENVIRONMENT === 'production'

    function checkUtm(nextState, replace) {
        const query = nextState.location.query
        const query_keys = Object.keys(query)
        if (! query_keys.length) return

        const utm_keys = [
            'utm_campaign',
            'utm_source',
            'utm_medium',
            'utm_term',
            'utm_content',
        ]

        // see if the query contains any UTM keys
        const utms = query_keys.filter(key => _.contains(utm_keys, key.toLowerCase()))
        // if not, no need to modify the URL
        if (! utms.length) return

        // rebuild the query, removing UTM keys
        let cleaned_query = (
            query_keys
            .filter(key => ! _.contains(utm_keys, key.toLowerCase()))
            .map(key => `${key}=${query[key]}`)
            .join('&')
        )
        // adding ? when query is not empty
        if (cleaned_query) cleaned_query = `?${cleaned_query}`

        replace({
            pathname: `${nextState.location.pathname}${cleaned_query}`,
        })

    }

    function requireAuth(nextState, replace) {
        if (! logged_in) {
            let came_from = nextState.location.pathname
            const query = Query.stringify(nextState.location.query)
            if (query) {
                came_from = `${came_from}${encodeURIComponent('?')}${encodeURIComponent(query)}`
            }

            replace({
                pathname: '/login',
                query: {
                    came_from,
                },
            })
        }
    }

    function requireAnon(nextState, replace) {
        if (logged_in) {
            replace({
                pathname: `/${viewer.user_name}`,
                query: nextState.location.query,
            })
        }
    }

    function onEnterHomepage(nextState, replace) {
        if (logged_in) {
            requireAnon(nextState, replace)
        } else {
            checkUtm(nextState, replace)
        }
    }

    function onlyAllowOwnerToVisitSubpaths(nextState, replace) {
        const user_name = nextState.params.user_name
        if (! isUser(viewer, user_name)) {
            replace({
                pathname: `/${user_name}`,
            })
        }
    }

    function profileRoute(route_options) {
        const route = _.extend({}, route_options)
        route.page_id = 'profile'
        // all profile routes have this onEnter handler
        route.onEnter = (nextState, replace) => {
            // profile routes can provide an additional onEnter handler
            // which will be called if the profile complete check passes
            if (route_options.onEnter) route_options.onEnter(nextState, replace)
        }
        route.getComponent = function getComponent(loc, cb) {
            require.ensure([], () => {
                cb(null, require('page/profile/validate_username'))
            }, 'profile')
        }
        return route
    }

    function requireBroadlyPrivateDomain(nextState, replace) {
        if (! logged_in) {
            return replace({
                pathname: '/team_login',
                query: {
                    came_from: '/@broadly.com',
                },
            })
        }

        if (! viewer || viewer.private_domain !== 'broadly.com') {
            return replace({
                pathname: '/',
                query: nextState.location.query,
            })
        }
    }

    function requireProductionAdmin(nextState, replace) {
        // allow the route to anyone if not in production
        if (! is_production) return

        // if production user is not logged in, redirect to homepage
        if (! logged_in) {
            return replace({
                pathname: '/',
                query: nextState.location.query,
            })
        }

        // if production user is not admin, redirect to their page
        if (! isAdmin(viewer)) {
            return replace({
                pathname: '/user',
                query: nextState.location.query,
            })
        }
    }

    function redirectToUserOrHomepage({ from, to }) {
        return {
            redirect: true, // mark this route as a redirect for tests
            path: from,
            onEnter(nextState, replace) {
                // anonymous users to homepage, auth'd to `to`
                if (logged_in) {
                    replace({
                        pathname: to,
                        query: nextState.location.query,
                    })
                } else {
                    replace({
                        pathname: '/',
                        query: nextState.location.query,
                    })
                }
            },
        }
    }

    function userRedirect(nextState, replace) {
        if (logged_in) {
            const pathname = nextState.location.pathname.replace(/^\/user/, `/${viewer.user_name}`)
            replace({
                pathname,
                query: nextState.location.query,
            })
        } else {
            requireAuth(nextState, replace)
        }
    }

    const routes = {
        component: App,
        path: '/',
        indexRoute: {
            page_id: 'home',
            onEnter: onEnterHomepage,
            getComponent(loc, cb) {
                if (funnels.get('20190116_homepage').is('experiment')) {
                    require.ensure([], () => {
                        cb(null, require('page/home2/home2'))
                    }, 'homepage2')
                } else {
                    require.ensure([], () => {
                        cb(null, require('page/home/home'))
                    }, 'homepage')
                }
            },
        },
        childRoutes: [
            // note: only need slash asterisk (/about/*)
            // if also redirecting a different child route (/about/team).
            // otherwise, all child routes are included by default.

            // misc redirects
            redirect({ from: 'blog', to: 'https://blog.about.me' }),
            redirect({ from: 'campus', to: 'https://campus.about.me' }),
            redirect({ from: 'support', to: 'https://support.about.me' }),
            redirect({ from: 'campuspromo', to: '/signup?promo=campus' }),
            redirect({ from: 'job', to: '/jobs' }),
            redirect({ from: 'directory', to: '/' }),
            redirect({ from: 'people', to: '/' }),
            redirect({ from: 'people/*', to: '/' }),
            redirect({ from: 'search', to: '/' }),
            redirect({ from: 'new_user', to: '/signup' }),
            redirect({ from: 'register', to: '/signup' }),
            redirect({ from: 'signup2', to: '/signup' }),

            // safari change password URI
            // https://github.com/aboutdotme/main/issues/6327
            redirect({ from: '.well-known/change-password', to: '/user/edit/password' }),

            // /user (or homepage) redirects
            redirect({ from: 'account', to: '/user/edit/account' }),
            redirect({ from: 'favs', to: '/' }),
            redirect({ from: 'findfriends', to: '/' }),
            redirect({ from: 'home', to: '/' }),
            redirect({ from: 'invite', to: '/' }),
            redirect({ from: 'intern(s)', to: '/' }),
            redirect({ from: 'mapped', to: '/' }),
            redirect({ from: 'promoted', to: '/' }),
            redirect({ from: 'share', to: '/user/edit/publish' }),
            redirect({ from: 'student(s)', to: '/' }),
            redirect({ from: 'student(s)/*', to: '/' }),
            redirect({ from: 'spotlight', to: '/user/edit/spotlight' }),

            // advisor redirects
            redirect({ from: 'about/advisors', to: '/' }),
            redirect({ from: 'aboutmeadvisors', to: '/' }),
            redirect({ from: 'advisor', to: '/' }),
            redirect({ from: 'ouradvisors', to: '/' }),
            redirect({ from: 'advisors', to: '/' }),
            redirect({ from: 'discover', to: '/' }),

            // /team redirects
            redirect({ from: 'about', to: '/team' }),
            // must be after other /about/thing child redirects (like about/advisors)
            redirect({ from: 'about/*', to: '/team' }),
            redirect({ from: 'aboutme', to: '/team' }),
            redirect({ from: 'aboutmeteam', to: '/team' }),
            redirect({ from: 'aboutus', to: '/team' }),
            redirect({ from: 'ourteam', to: '/team' }),
            redirect({ from: 'us', to: '/team' }),

            // partners redirects
            redirectToUserOrHomepage({ from: 'partner(s)', to: '/user' }),
            redirectToUserOrHomepage({ from: 'partner(s)/*', to: '/user' }),
            redirectToUserOrHomepage({ from: 'greatness', to: '/user' }),

            // press urls currently go to /team, but will go to /press
            redirect({ from: 'media', to: '/press' }),
            redirect({ from: 'ourpress', to: '/press' }),

            // /user/edit/publish redirects
            redirectToUserOrHomepage({ from: 'backstory', to: '/user/edit/publish' }),
            redirectToUserOrHomepage({ from: 'statistics', to: '/user/edit/publish' }),
            redirectToUserOrHomepage({ from: 'stat(s)', to: '/user/edit/publish' }),
            redirectToUserOrHomepage({ from: 'editingtools', to: '/user/edit/publish' }),
            redirectToUserOrHomepage({ from: 'intro', to: '/user/edit/publish' }),
            redirectToUserOrHomepage({ from: 'applewatch', to: '/user/edit/publish' }),
            redirectToUserOrHomepage({ from: 'dashboard', to: '/user/edit/publish' }),

            {
                path: 'assets',
                page_id: 'assets',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/assets/assets'))
                    }, 'assets')
                },
            },
            {
                path: 'emailsignature',
                page_id: 'emailsignature',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/emailsignature/emailsignature'))
                    }, 'emailsignature')
                },
            },
            {
                path: 'features(/:subroute)',
                page_id: 'features',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/features/features'))
                    }, 'features')
                },
            },
            {
                path: 'virtualtwin',
                page_id: 'virtualtwin',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/virtualtwin/virtualtwin'))
                    }, 'virtualtwin')
                },
            },
            {
                path: 'jobs(/:title)',
                page_id: 'jobs',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/jobs/jobs'))
                    }, 'jobs')
                },
            },
            {
                path: 'login',
                page_id: 'login',
                onEnter: requireAnon,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/login/login_react'))
                    }, 'login')
                },
            },
            {
                path: 'team_login',
                page_id: 'team_login',
                onEnter: requireAnon,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/team/login_react'))
                    }, 'team_login')
                },
            },
            {
                path: 'gdpr_delete',
                page_id: 'gdpr_delete',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/gdpr_delete/gdpr_delete'))
                    }, 'gdpr_delete')
                },
            },
            {
                path: 'mapped_nav',
                page_id: 'mapped_nav',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/mapped_nav/mapped_nav'))
                    }, 'mapped_nav')
                },
            },

            {
                path: 'c/playground',
                page_id: 'playground',
                onEnter: requireProductionAdmin,
                indexRoute: {
                    onEnter(nextState, replace) {
                        replace({
                            pathname: '/c/playground/glyphs',
                            query: nextState.location.query,
                        })
                    },
                },
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/playground/playground'))
                    }, 'playground')
                },
                getChildRoutes(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/playground/playground_routes'))
                    }, 'playground')
                },
            },
            {
                path: 'press',
                page_id: 'press',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/press/press_react'))
                    }, 'press')
                },
            },
            {
                path: 'pricing',
                page_id: 'pricing',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/pricing/pricing'))
                    }, 'pricing')
                },
            },
            {
                path: 'report_spam',
                page_id: 'report_spam',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/report_spam/report_react'))
                    }, 'report_spam')
                },
            },
            // for some reason, path: 'signup(/*)' doesn't work!
            // have to do 'signup' and 'signup/*' as separate routes
            {
                path: 'signup',
                page_id: 'signup',
                onEnter: requireAnon,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/signup/signup_react'))
                    }, 'signup')
                },
            },
            {
                path: 'signup/*',
                page_id: 'signup',
                onEnter: requireAnon,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/signup/signup_react'))
                    }, 'signup')
                },
            },
            {
                path: 'recent',
                page_id: 'recent',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/recent/recent'))
                    }, 'recent_sites')
                },
            },
            {
                path: 'recent/pro',
                page_id: 'recent_pro',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/recent/recent'))
                    }, 'recent_sites')
                },
            },
            {
                path: 'recent/spam',
                page_id: 'spam',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/recent/spam'))
                    }, 'spam')
                },
            },
            {
                path: 'recent/ham',
                page_id: 'ham',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    // uses the spam page component, but loads ham users
                    require.ensure([], () => {
                        cb(null, require('page/recent/spam'))
                    }, 'spam')
                },
            },
            {
                path: 'reset_password(/:reset_id)',
                page_id: 'reset_password',
                onEnter: requireAnon,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/reset_password/reset_password_react'))
                    }, 'reset_password')
                },
            },
            {
                path: 'side-hustlers',
                page_id: 'side-hustlers',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/side_hustlers/side_hustlers'))
                    }, 'side-hustlers')
                },
            },
            {
                path: 'slack(/:subroute)',
                page_id: 'slack',
                getComponent(nextState, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/slack/slack'))
                    }, 'slack')
                },
            },
            {
                path: 'team',
                page_id: 'team',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/team/team_react'))
                    }, 'team')
                },
            },
            {
                path: '@broadly.com',
                page_id: 'broadly_team',
                onEnter: requireBroadlyPrivateDomain,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/team/broadly_react'))
                    }, 'broadly')
                },
            },
            {
                path: 'unsubscribe(/:type)',
                page_id: 'unsubscribe',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/unsubscribe/unsubscribe_react'))
                    }, 'unsubscribe')
                },
            },
            {
                path: 'update',
                page_id: 'update',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/signup/migration_react'))
                    }, 'migration')
                },
            },
            {
                path: 'update/accept_terms',
                page_id: 'accept_terms',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/accept_terms/accept_terms_react'))
                    }, 'accept_terms')
                },
            },
            {
                path: 'update/*',
                page_id: 'update',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/signup/migration_react'))
                    }, 'migration')
                },
            },

            // All legal routes
            redirect({ from: 'cookies', to: '/legal/cookies' }),
            {
                path: 'legal/cookies',
                page_id: 'cookies',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/legal/cookies/cookies'))
                    }, 'cookies')
                },
            },
            redirect({ from: 'copyright', to: '/legal/copyright' }),
            {
                path: 'legal/copyright',
                page_id: 'copyright',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/legal/copyright/copyright'))
                    }, 'copyright')
                },
            },
            redirect({ from: 'privacy', to: '/legal/privacy' }),
            {
                path: 'legal/privacy',
                page_id: 'privacy',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/legal/privacy/privacy'))
                    }, 'privacy')
                },
            },
            {
                path: 'legal/privacy/eu',
                page_id: 'privacy_eu',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/legal/privacy/eu'))
                    }, 'privacy_eu')
                },
            },
            redirect({ from: 'terms', to: '/legal/terms' }),
            redirect({ from: 'legal', to: '/legal/terms' }),
            {
                path: 'legal/terms',
                page_id: 'terms',
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/legal/terms/terms'))
                    }, 'terms')
                },
            },
            // end legal routes

            // disabling /upgrade for now, redirecting to /pricing
            // {
            //     path: 'upgrade',
            //     page_id: 'upgrade',
            //     getComponent(loc, cb) {
            //         require.ensure([], () => {
            //             cb(null, require('page/upgrade/upgrade'))
            //         }, 'upgrade')
            //     },
            // },
            // {
            //     path: 'upgrade/*',
            //     page_id: 'upgrade',
            //     onEnter: onEnterUpgrade,
            //     getComponent(loc, cb) {
            //         require.ensure([], () => {
            //             cb(null, require('page/upgrade/upgrade'))
            //         }, 'upgrade')
            //     },
            // },
            redirect({ from: 'upgrade', to: '/pricing' }),

            {
                path: 'welcome',
                page_id: 'home',
                // redirect: true,
                onEnter: onEnterHomepage,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/home/home_variant_from_profile'))
                    }, 'homepage')
                },
            },
            {
                // This thing just closes the current window.
                path: 'WfzAVDrhQLZT4BVdmjofPpvMvhXR',
                page_id: 'closer',
                onEnter: requireAuth,
                getComponent(loc, cb) {
                    require.ensure([], () => {
                        cb(null, require('page/closer/closer'))
                    }, 'closer')
                },
            },

            // /user redirect
            {
                path: 'user',
                redirect: true,
                onEnter: userRedirect,
            },
            {
                path: 'user/*',
                redirect: true,
                onEnter: userRedirect,
            },

            // profile routes

            // only allow page owner to visit certain subpaths
            profileRoute({
                path: ':user_name/edit',
                onEnter: onlyAllowOwnerToVisitSubpaths,
            }),
            profileRoute({
                path: ':user_name/edit/*',
                onEnter: onlyAllowOwnerToVisitSubpaths,
            }),
            profileRoute({
                path: ':user_name/getstarted',
                onEnter: onlyAllowOwnerToVisitSubpaths,
            }),
            profileRoute({
                path: ':user_name/getstarted/*',
                onEnter: onlyAllowOwnerToVisitSubpaths,
            }),

            profileRoute({
                path: ':user_name/verify',
            }),
            profileRoute({
                path: ':user_name/verify/*',
            }),

            // most username/spotlight URLs will redirect to the spotlight URL.
            // but in-page spotlights should just show the profile
            profileRoute({
                path: ':user_name/spotlight',
            }),

            // redirect messy URLs to /username
            profileRoute({
                path: ':user_name/*',
                onEnter(nextState, replace) {
                    const user_name = nextState.params.user_name
                    replace({
                        pathname: `/${user_name}`,
                        query: nextState.location.query,
                    })
                },
            }),

            // redirect URLs with utm query to /username
            profileRoute({
                path: ':user_name',
                onEnter: checkUtm,
            }),
        ],
    }

    // non-production routes
    if (! is_production) {
        // user tester route
        // eslint-disable-next-line no-inner-declarations
        function profileTesterRoute(route_options) {
            const route = _.extend({}, route_options)
            route.page_id = 'profile_tester'
            // all profile routes have this onEnter handler
            route.onEnter = requireProductionAdmin
            route.getComponent = function getComponent(loc, cb) {
                require.ensure([], () => {
                    cb(null, require('page/profile_tester/profile_tester'))
                }, 'profile-tester')
            }
            return route
        }

        routes.childRoutes.unshift({
            path: 'experiment',
            page_id: 'experiment',
            getComponent(loc, cb) {
                require.ensure([], () => {
                    cb(null, require('page/experiment/experiment'))
                }, 'page-experiment')
            },
        })
        routes.childRoutes.unshift(profileTesterRoute({ path: 'u' }))
        routes.childRoutes.unshift(profileTesterRoute({ path: 'u/edit' }))
        routes.childRoutes.unshift(profileTesterRoute({ path: 'u/edit/*' }))
        routes.childRoutes.unshift(profileTesterRoute({ path: 'u/getstarted' }))
        routes.childRoutes.unshift(profileTesterRoute({ path: 'u/getstarted/*' }))
        routes.childRoutes.unshift(profileTesterRoute({ path: 'u/portfolio' }))
        routes.childRoutes.unshift(
            redirect({ from: 'newuser', to: '/n/internal/dev/new_user' })
        )
    }

    return routes

}

// for testing with a simpler route config

// function Profile(props) {
//     return <div />
// }
//
// const simple_routes = (options) => {
//     return (
//         <Route path="/" component={App}>
//             <Redirect from="about(/*)" to="aboutmeteam" />
//             <Route path=":user_name" component={Profile} page_id="profile" />
//         </Route>
//     )
// }

// end for testing with a simpler route config

function getMappedRoutes(options) {
    return (
        <Route path="/" component={App}>
            <IndexRoute
                page_id="profile"
                getComponent={(loc, cb) => {
                    require.ensure([], () => {
                        cb(null, require('page/profile/validate_mapped'))
                    }, 'profile')
                }}
                />
            <Redirect from="*" to="/" />
        </Route>
    )
}

function getRoutes(options) {
    if (options.globals.MAPPED) {
        return getMappedRoutes(options)
    }

    return getDefaultRoutes(options)
}

module.exports = {
    getRoutes,
    getDefaultRoutes,
    getMappedRoutes,
}
